<template>
  <div class="login-container">
    <!-- Left Panel -->
    <div class="login-left">
      <div class="left-bg-2"></div>
      <div class="left-content">
        <img src="@/assets/images/login/image_3.png" class="brand-logo" alt="JDENERGY" />
        <div class="brand-name">JDENERGY</div>
        <div class="slogan-container">
          <div class="slogan-box">
            <div class="slogan-text">让稳定的清洁电力惠及每一个人</div>
            <div class="domain-text">jdenergy.com</div>
          </div>
        </div>
        <!-- Footer -->
        <div class="login-footer-new">
          <div class="footer-text">{{ $str.getKey('登录/网站备案/许可证号') }}： <a target="_blank"
              href="https://beian.miit.gov.cn/#/Integrated/recordQuery">陕ICP备20010223号-3</a></div>
          <div class="footer-links">
            <span>Copyright © {{ year }} {{ $str.getKey('登录/奇点能源') }} All Rights Reserved.</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Panel -->
    <div class="login-right">
      <div class="login-card">
        <div class="login-header">
          <div class="welcome-text">欢迎来到奇点能源</div>
          <div class="welcome-sub">Welcome to jdenergy</div>
        </div>

        <div class="switch-container">
          <v-btn 
            block 
            class="switch-btn" 
            variant="flat" 
            @click="toggleLoginType"
          >
            {{ loginType === 'account' ? '手机号验证码登录' : '账号密码登录' }}
          </v-btn>
        </div>

        <div class="separator">
           <span class="line"></span>
           <span class="text">or</span>
           <span class="line"></span>
        </div>

        <v-form v-if="loginType === 'account'" v-model="valid" @submit.prevent="onSubmit" :disabled="loading" class="login-form-custom">
          <!-- Username -->
          <div class="input-group">
            <label class="input-label">账号<span class="required">*</span></label>
            <v-text-field v-model="loginForm.username" :rules="nameRules" placeholder="请输入您的账号" variant="outlined"
              class="custom-input" color="primary" ></v-text-field>
          </div>

          <!-- Password -->
          <div class="input-group">
            <label class="input-label">密码<span class="required">*</span></label>
            <v-text-field 
              v-model="loginForm.password" 
              :rules="passwordRules" 
              placeholder="请输入您的密码" 
              variant="outlined"
              :type="showPassword ? 'text' : 'password'" 
              class="custom-input" 
              color="primary" 
              
            >
              <template #append-inner>
                <v-icon
                  :icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'" 
                  @click="showPassword = !showPassword"
                  style="cursor: pointer;"
                  size="small"
                  tabindex="-1"
                ></v-icon>
              </template>
            </v-text-field>
          </div>

          <!-- Captcha -->
          <div class="input-group" v-if="captchaEnabled">
            <label class="input-label">验证码<span class="required">*</span></label>
            <div class="captcha-row">
              <v-text-field v-model="loginForm.code" :rules="codeRules" placeholder="请输入验证码" variant="outlined"
                class="custom-input captcha-input" color="primary" ></v-text-field>
              <div class="captcha-img-box">
                <img :src="codeUrl" @click="getCode" class="login-code-img" alt="验证码" />
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div class="actions-row">
            <div class="remember-me">
              <v-checkbox v-model="loginForm.rememberMe" hide-details density="compact" color="#3A68FF"
                class="custom-checkbox">
                <template v-slot:label>
                  <span class="remember-text">记住密码</span>
                </template>
              </v-checkbox>
            </div>
            <!-- <div class="forgot-pwd">忘记密码?</div> -->
          </div>

          <!-- Login Button -->
          <v-btn block class="login-btn-custom" type="submit" :loading="loading">
            登 录
          </v-btn>
        </v-form>

        <v-form v-if="loginType === 'phone'" v-model="phoneValid" @submit.prevent="onPhoneLogin" :disabled="loading" class="login-form-custom">
          <!-- Phone Number -->
          <div class="input-group">
            <label class="input-label">手机号<span class="required">*</span></label>
            <v-text-field v-model="phoneForm.phoneNumber" :rules="phoneRules" placeholder="请输入手机号" variant="outlined"
              class="custom-input" color="primary" ></v-text-field>
          </div>

          <!-- Verification Code -->
          <div class="input-group">
            <label class="input-label">验证码<span class="required">*</span></label>
            <v-text-field v-model="phoneForm.code" :rules="phoneCodeRules" placeholder="请输入验证码" variant="outlined"
              class="custom-input" color="primary" >
                <template #append-inner>
                   <span 
                    class="get-code-text" 
                    :class="{ 'disabled': !isPhoneNumberValid || isCountingDown }"
                    @click="getSmsCode"
                  >
                    {{ buttonText }}
                  </span>
                </template>
            </v-text-field>
          </div>

          <!-- Actions -->
          <div class="actions-row">
            <div class="remember-me">
              <v-checkbox v-model="loginForm.rememberMe" hide-details density="compact" color="#3A68FF"
                class="custom-checkbox">
                <template v-slot:label>
                  <span class="remember-text">记住密码</span>
                </template>
              </v-checkbox>
            </div>
          </div>

          <!-- Login Button -->
          <v-btn block class="login-btn-custom" type="submit" :loading="loading">
            登 录
          </v-btn>
        </v-form>
      </div>
      <!-- <div style="position:fixed; top: 16px; right: 40px; z-index: 10;">
        <LanguageChangeButton style="color: #333;" />
      </div> -->
    </div>
  </div>
</template>

<script setup>
import { getCodeImg, getCodePhone } from "@/api/login";
import Cookies from "js-cookie";
import { encrypt, decrypt } from "@/utils/jsencrypt";
import useUserStore from '@/store/modules/user'
// import Particles from 'particlesjs' // Removed particles
import { computed, getCurrentInstance, ref } from "vue";
import useAppStore from "@/store/modules/app";

import { getToken, setToken, removeToken } from '@/utils/auth'
import { eui } from "ems-ui";
import LanguageChangeButton from "@/components/LanguageChangeButton";
import { useRouter } from "vue-router";
import usePermissionStore from "@/store/modules/permission";

const $str = window.$str
const year = (new Date).getFullYear()


const appStore = useAppStore()
const userStore = useUserStore()
const router = useRouter()
const { proxy } = getCurrentInstance()

const loginType = ref('account')
function toggleLoginType() {
  loginType.value = loginType.value === 'account' ? 'phone' : 'account'
}
const phoneValid = ref(false)
const phoneForm = ref({
  phoneNumber: "",
  code: ""
})
const isCountingDown = ref(false)
const countdown = ref(30)
const buttonText = ref("获取验证码")

const phoneRules = [
  v => !!v || '请输入手机号',
  v => /^1[3-9]\d{9}$/.test(v) || '请输入有效的手机号'
]
const phoneCodeRules = [
  v => !!v || '请输入验证码'
]

const isPhoneNumberValid = computed(() => {
  return /^1[3-9]\d{9}$/.test(phoneForm.value.phoneNumber)
})

const valid = ref(false) // Added valid ref for v-form
const showPassword = ref(false) // Control password visibility

const loginForm = ref({
  username: "",
  password: "",
  rememberMe: false,
  code: "",
  uuid: "",
});

const nameRules = [
  value => {
    if (value) return true
    return $str.getKey("登录/请输入您的账号")
  },
]

const passwordRules = [
  value => {
    if (value) return true
    return $str.getKey("登录/请输入您的密码")
  },
]

const codeRules = [
  value => {
    if (value) return true
    return $str.getKey("登录/请输入验证码")
  },
]

const loading = ref(false)

async function getSmsCode() {
  if (!isPhoneNumberValid.value || isCountingDown.value) return;

  try {
    await getCodePhone({ phoneNumber: phoneForm.value.phoneNumber });
    eui.showToast('验证码发送成功', 'success');

    isCountingDown.value = true;
    countdown.value = 30;
    buttonText.value = `${countdown.value}秒后重新获取`;

    const timer = setInterval(() => {
      countdown.value--;
      buttonText.value = `${countdown.value}秒后重新获取`;
      if (countdown.value <= 0) {
        clearInterval(timer);
        isCountingDown.value = false;
        buttonText.value = "获取验证码";
      }
    }, 1000);
  } catch (error) {
    console.error(error);
  }
}

async function onPhoneLogin(event) {
  const results = await event
  if (results.valid) {
    eui.showLoading('登录中...')
    loading.value = true
    try {
      await userStore.phoneLogin(phoneForm.value)
      const infoRes = await userStore.getInfo()
      if (infoRes?.msg) {
        eui.showToast(infoRes.msg, 'success')
      }

      router.push(redirect.value || "/index");
    } catch (error) {
      console.error(error)
    } finally {
      eui.hideLoading()
      loading.value = false
    }
  }
}

async function onSubmit(event) {
  const results = await event
  if (results.valid) {
    eui.showLoading('登录中...')
    loading.value = true
    if (loginForm.value.rememberMe) {
      Cookies.set("username", loginForm.value.username, { expires: 30 });
      Cookies.set("password", encrypt(loginForm.value.password), { expires: 30 });
      Cookies.set("rememberMe", loginForm.value.rememberMe, { expires: 30 });
    } else {
      Cookies.remove("username");
      Cookies.remove("password");
      Cookies.remove("rememberMe");
    }
    try {
      await userStore.login(loginForm.value)
      const infoRes = await userStore.getInfo()
      if (infoRes?.msg) {
        eui.showToast(infoRes.msg, 'success')
      }

      appStore.setStationId(null)
      // 登录后预拉电站列表：layout 挂载时也会调用,getStations 内部已做并发去重,不会重复请求
      appStore.getStations()

      router.push(redirect.value || "/index");

    } catch (error) {
      console.error(error)
      if (captchaEnabled.value) {
        getCode();
      }
    } finally {
      eui.hideLoading()
      loading.value = false
    }
  }
}
const codeUrl = ref("");
const captchaEnabled = ref(true);
const redirect = computed(() => {
  return router.currentRoute.value.query.redirect;
});


function getCode() {
  getCodeImg().then(res => {
    captchaEnabled.value = res.captchaEnabled === undefined ? true : res.captchaEnabled;
    if (captchaEnabled.value) {
      codeUrl.value = "data:image/gif;base64," + res.img;
      loginForm.value.uuid = res.uuid;
    }
  });
}

function getCookie() {
  const username = Cookies.get("username");
  const password = Cookies.get("password");
  const rememberMe = Cookies.get("rememberMe");
  loginForm.value = {
    username: username === undefined ? loginForm.value.username : username,
    password: password === undefined ? loginForm.value.password : decrypt(password),
    rememberMe: rememberMe === undefined ? false : Boolean(rememberMe)
  };
}

getCode()
getCookie()

// Removed Particles logic
</script>

<style lang='scss' scoped>
.login-container {
  display: flex;
  width: 100vw;
  height: 100vh;
  background: linear-gradient(107.82deg, #E5F6FF 0%, #F8F8F8 49.04%, #F1FFFC 100%);
  overflow: hidden;
  font-family: 'HarmonyOS Sans SC', 'PingFang SC', sans-serif;

  :deep(.v-field__field) {
    border-radius: 14px 0 0 14px !important;
    overflow: hidden !important;
  }

  :deep(input) {
    background-color: transparent !important;
  }
}

/* Left Panel Styles */
.login-left {
  position: relative;
  width: 50%;
  /* Adjust based on design ratio 965/1920 ~ 50% */
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 0 0 178px 0;
  overflow: hidden;

  @media (max-width: 960px) {
    display: none;
  }
}


.left-bg-2 {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url("@/assets/images/login/image_2.png");
  background-size: cover;
  background-position: center;
  z-index: 2;
}

.left-content {
  position: relative;
  z-index: 3;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.brand-logo {
  width: 209px;
  height: 176px;
  margin-top: 28vh;
  margin-left: 18vw;
}

.brand-name {
  font-family: 'Poppins', sans-serif;
  font-size: 50px;
  font-weight: 600;
  color: #FFFFFF;
  margin-top: 21px;
  margin-left: 18vw;
  line-height: 150%;
}

.slogan-container {
  margin-top: 73px;
  margin-left: 12vw;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 471px;
}

.slogan-box {
  width: 471px;
  height: 126px;
  border-radius: 26px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  backdrop-filter: blur(4px);
}

.slogan-text {
  font-size: 18px;
  color: #FFFFFF;
  margin-bottom: 6px;
}

.domain-text {
  font-size: 29px;
  font-weight: 700;
  color: #FFFFFF;
}

/* Right Panel Styles */
.login-right {
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.login-card {
  width: 534px;
  height: auto;
  min-height: 600px;
  background-color: #FFFFFF;
  border-radius: 20px;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.05);
  /* Approximate shadow */
  padding: 60px 58px;
  display: flex;
  flex-direction: column;
  background-size: cover;
  background-repeat: no-repeat;
  margin-bottom: 40px;
}

.login-header {
  margin-bottom: 35px;
}

.welcome-text {
  font-size: 36px;
  font-weight: 700;
  color: #2E2E2E;
  margin-bottom: 8px;
}

.welcome-sub {
  font-family: 'DM Sans', sans-serif;
  font-size: 16px;
  color: #A3AED0;
}

.login-form-custom {
  width: 100%;
}

.input-group {
  margin-bottom: 24px;
}

.input-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #2B3674;
  margin-bottom: 12px;
}

.required {
  color: #3A68FF;
  margin-left: 4px;
}

.custom-input :deep(.v-field__outline__start),
.custom-input :deep(.v-field__outline__end),
.custom-input :deep(.v-field__outline__notch) {
  border-color: #D4D9E5 !important;
}

.custom-input :deep(.v-field--variant-outlined .v-field__outline) {
  border-radius: 16px;
}

.custom-input :deep(.v-field__input) {
  padding-top: 14px;
  padding-bottom: 14px;
  min-height: 50px;
}

.captcha-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.captcha-input {
  flex: 1;
}

.captcha-img-box {
  height: 50px;
  width: 120px;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #D4D9E5;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f0f0;
  align-self: self-start;
}

.login-code-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
}

.actions-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
}

.remember-text {
  font-size: 14px;
  color: #2B3674;
}

.forgot-pwd {
  font-size: 14px;
  color: #3A68FF;
  font-weight: 500;
  cursor: pointer;
}

.login-btn-custom {
  width: 100%;
  height: 54px !important;
  border-radius: 20px !important;
  background-color: #3A68FF !important;
  color: #FFFFFF !important;
  font-size: 14px;
  font-weight: 700;
  text-transform: none;
  letter-spacing: 0;
}

/* Footer */
.login-footer-new {
  width: 100%;
  text-align: center;
  color: rgba(255, 255, 255, 0.70);
  font-size: 14px;
  position: absolute;
  bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.footer-text a {
  color: rgba(255, 255, 255, 0.70);
  text-decoration: none;
}

/* 适配较小屏幕 */
@media (max-width: 1400px), (max-height: 800px) {
  .login-card {
    width: 420px;
    padding: 40px;
    min-height: 520px;
  }

  .login-header {
    margin-bottom: 24px;
  }

  .welcome-text {
    font-size: 28px;
  }

  .welcome-sub {
    font-size: 14px;
  }

  .input-group {
    margin-bottom: 18px;
  }

  .input-label {
    font-size: 13px;
    margin-bottom: 8px;
  }

  .actions-row {
    margin-bottom: 24px;
  }

  .custom-input :deep(.v-field__input) {
    padding-top: 12px;
    padding-bottom: 12px;
    min-height: 44px;
  }

  .login-btn-custom {
    height: 48px !important;
    font-size: 13px;
  }

  .switch-btn {
    height: 44px !important;
    font-size: 13px;
  }

  .captcha-img-box {
    height: 44px;
    width: 100px;
  }

  /* 左侧面板适配 */
  .brand-logo {
    width: 150px;
    height: auto;
    margin-top: 15vh;
    margin-left: 15vw;
  }

  .brand-name {
    font-size: 36px;
    margin-top: 14px;
    margin-left: 15vw;
  }

  .slogan-container {
    margin-top: 45px;
    margin-left: 10vw;
    width: 350px;
  }

  .slogan-box {
    width: 350px;
    height: 95px;
  }

  .slogan-text {
    font-size: 15px;
  }

  .domain-text {
    font-size: 22px;
  }

  .login-footer-new {
    font-size: 12px;
    bottom: 15px;
  }
}

@media (max-width: 1200px) {
  .login-left {
    width: 45%;
  }
}

@media (max-width: 960px) {
  .login-left {
    display: none;
  }

  .login-card {
    width: 90%;
    max-width: 450px;
  }
}

@media (max-width: 600px) {
  .login-card {
    width: 90%;
    padding: 30px 20px;
  }
}

.switch-container {
  margin-bottom: 24px;
}

.switch-btn {
  background-color: #F4F7FF !important;
  color: #2E2E2E !important;
  font-weight: 500;
  font-size: 14px;
  height: 50px !important;
  border-radius: 16px !important;
  text-transform: none;
  letter-spacing: 0;
  box-shadow: none !important;

  &:hover {
    background-color: #EBF0FF !important;
  }
}

.separator {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  width: 100%;
}

.separator .line {
  flex: 1;
  height: 1px;
  background-color: #E0E5F2;
}

.separator .text {
  margin: 0 16px;
  font-size: 14px;
  color: #A3AED0;
}

.get-code-text {
  font-size: 14px;
  font-weight: 500;
  color: #3A68FF;
  cursor: pointer;
  user-select: none;
  padding-left: 8px;
  white-space: nowrap;

  &.disabled {
    color: #A3AED0;
    cursor: not-allowed;
  }

  &:hover:not(.disabled) {
    opacity: 0.8;
  }
}

/* 适配大屏幕 */
@media (min-width: 2560px) {
  .login-card {
    width: 800px;
    min-height: 750px;
    padding: 80px 70px;
    border-radius: 28px;
  }

  .login-header {
    margin-bottom: 40px;
  }

  .welcome-text {
    font-size: 44px;
  }

  .welcome-sub {
    font-size: 18px;
  }

  .input-group {
    margin-bottom: 28px;
  }

  .input-label {
    font-size: 16px;
    margin-bottom: 14px;
  }

  .custom-input :deep(.v-field__input) {
    padding-top: 18px;
    padding-bottom: 18px;
    min-height: 60px;
    font-size: 16px;
  }

  .captcha-img-box {
    height: 60px;
    width: 140px;
  }

  .actions-row {
    margin-bottom: 32px;
  }

  .remember-text {
    font-size: 15px;
  }

  .login-btn-custom {
    height: 64px !important;
    font-size: 16px;
    border-radius: 24px !important;
  }

  .switch-btn {
    height: 56px !important;
    font-size: 16px;
    border-radius: 20px !important;
  }

  .separator {
    margin-bottom: 28px;
  }

  .separator .text {
    font-size: 15px;
    margin: 0 20px;
  }

  .get-code-text {
    font-size: 15px;
    padding-left: 10px;
  }
}
</style>
